import { useQuery } from '@tanstack/react-query';
import {
  INCIDENT_DEFAULT_FIELDS,
  INCIDENT_V2_DEFAULT_TICKET_FIELDS,
} from '@elementary/common-ui/constants/queryKeys';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import { fetchDefaultIncidentFields } from 'incidents/api';
import { fetchDefaultIncidentV2Fields } from 'incidents/incident-v2/api';
import { TicketingIntegration } from 'shared/types';
import { CreateTicketDialog } from 'shared/components/ticketing';

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (customFields?: Record<string, any>) => void;
  incidentId: string;
  integration: TicketingIntegration;
  envId: string;
  incidentUrl: string;
  isCreating: boolean;
  error?: Error | null;
};

const IncidentTicketCreateDialog = ({
  open,
  onClose,
  onConfirm,
  incidentId,
  integration,
  envId,
  incidentUrl,
  isCreating,
  error,
}: Props) => {
  const { data: account } = useAccount();
  const useV2 = !!account?.incidents_v2_enabled;
  const fetchDefaultFields = useV2
    ? fetchDefaultIncidentV2Fields
    : fetchDefaultIncidentFields;
  const defaultFieldsQueryKeyRoot = useV2
    ? INCIDENT_V2_DEFAULT_TICKET_FIELDS
    : INCIDENT_DEFAULT_FIELDS;

  const { data: incidentDefaultFields, isLoading: isLoadingDefaults } =
    useQuery({
      queryKey: [
        defaultFieldsQueryKeyRoot,
        envId,
        incidentId,
        integration.id,
        incidentUrl,
      ],
      queryFn: () =>
        fetchDefaultFields(envId, incidentId, integration.id, incidentUrl),
      enabled:
        open && !!envId && !!incidentId && !!incidentUrl && !!integration.id,
      select: (data) => data.default_fields.fields,
    });

  return (
    <CreateTicketDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      integration={integration}
      envId={envId}
      isCreating={isCreating}
      error={error}
      defaultFields={incidentDefaultFields}
      isLoadingDefaults={isLoadingDefaults}
      contextId={incidentId}
    />
  );
};

export default IncidentTicketCreateDialog;
